import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  HiOutlineArrowLeft,
  HiOutlineX,
  HiOutlineLockClosed,
  HiOutlineCheck,
} from 'react-icons/hi';
import Dialog from '../../ui/Dialog';
import LoginForm from '../../forms/LoginForm';
import CodeForm from '../../forms/CodeForm';
import CompleteForm from '../../forms/CompleteForm';

/**
 * Premium login modal. 3-step flow:
 *   1. Phone number
 *   2. Verification code
 *   3. Complete profile (name + email) if user is incomplete
 *
 * Success flash (check icon + fade) briefly before closing to reassure
 * the customer their sign-in worked before we navigate them back.
 */
const LoginModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { slug } = useSelector((state) => state.base.common.currentStore);
  const user = useSelector((state) => state.auth.user);
  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [isLoginComplete, setIsLoginComplete] = useState(false);
  const successTimerRef = useRef(null);

  // Reset state whenever the modal is reopened
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setPhone('');
      setShowSuccess(false);
      setIsLoginComplete(false);
    }
    return () => {
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
    };
  }, [isOpen]);

  const handlePhoneSubmit = (phoneNumber) => {
    setPhone(phoneNumber);
    setStep(2);
  };

  const handleCodeSubmit = () => {
    setIsLoginComplete(true);
  };

  useEffect(() => {
    if (!isLoginComplete || !user?.id) return;
    const needComplete =
      !user.email ||
      user.email === '' ||
      !user.fullname ||
      user.fullname === '';
    if (needComplete) {
      setStep(3);
      setIsLoginComplete(false);
    } else {
      setShowSuccess(true);
      successTimerRef.current = setTimeout(() => {
        onClose();
        setShowSuccess(false);
        setStep(1);
        setPhone('');
        setIsLoginComplete(false);
        navigate(`/${slug || 'home'}`);
      }, 1400);
    }
  }, [isLoginComplete, user, onClose, navigate, slug]);

  const handleCompleteSubmit = () => {
    setShowSuccess(true);
    successTimerRef.current = setTimeout(() => {
      onClose();
      setShowSuccess(false);
      setStep(1);
      setPhone('');
      navigate(`/${slug || 'home'}`);
    }, 1400);
  };

  const handleSkip = () => {
    onClose();
    setStep(1);
    setPhone('');
    navigate(`/${slug || 'home'}`);
  };

  const handleClose = () => {
    if (successTimerRef.current) clearTimeout(successTimerRef.current);
    onClose();
    setStep(1);
    setPhone('');
    setShowSuccess(false);
    setIsLoginComplete(false);
  };

  const handleBack = () => {
    setStep(1);
  };

  const totalSteps = step === 3 ? 3 : 2;
  const phoneMasked = phone ? `****${String(phone).slice(-4)}` : '';

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleClose}
      onRequestClose={handleClose}
      contentClassName="max-w-[440px] mx-auto rounded-[20px]"
      closable={false}
    >
      <div
        className="relative"
        style={{ fontFamily: 'Geist, system-ui, sans-serif' }}
      >
        <style>{`
          @keyframes lmFadeUp {
            from { opacity: 0; transform: translateY(8px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes lmPop {
            0% { transform: scale(0.7); opacity: 0; }
            60% { transform: scale(1.08); opacity: 1; }
            100% { transform: scale(1); }
          }
          .lm-fade { animation: lmFadeUp 0.42s cubic-bezier(0.2, 0.8, 0.2, 1) both; }
          .lm-pop { animation: lmPop 0.55s cubic-bezier(0.2, 1.3, 0.4, 1) both; }
          @media (prefers-reduced-motion: reduce) {
            .lm-fade, .lm-pop { animation: none; }
          }
        `}</style>

        {/* Close (top-right) */}
        {!showSuccess && (
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close"
            className="absolute grid place-items-center transition-all hover:bg-black/5"
            style={{
              top: '12px',
              right: '12px',
              width: '30px',
              height: '30px',
              borderRadius: '8px',
              background: 'transparent',
              color: 'var(--ui-ink-3)',
              border: 'none',
              cursor: 'pointer',
              zIndex: 2,
            }}
          >
            <HiOutlineX size={17} />
          </button>
        )}

        {/* Back (step 2) */}
        {step === 2 && !showSuccess && (
          <button
            type="button"
            onClick={handleBack}
            className="absolute inline-flex items-center gap-[5px] transition-colors hover:text-black/70"
            style={{
              top: '18px',
              left: '22px',
              fontSize: '12.5px',
              fontWeight: 600,
              color: 'var(--ui-ink-2)',
              background: 'transparent',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              fontFamily: 'inherit',
              zIndex: 2,
            }}
          >
            <HiOutlineArrowLeft size={13} />
            Back
          </button>
        )}

        <div
          style={{
            padding:
              step === 2 && !showSuccess
                ? '48px 26px 26px'
                : '26px 26px 26px',
          }}
        >
          {showSuccess ? (
            <SuccessFlash />
          ) : (
            <>
              {/* Step badge */}
              <div
                className="lm-fade mb-[6px]"
                style={{
                  fontSize: '10.5px',
                  fontFamily: 'Geist Mono, monospace',
                  fontWeight: 700,
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase',
                  color: 'var(--accent, var(--site-color))',
                }}
              >
                Step {step} of {totalSteps}
              </div>

              {step === 1 && (
                <div className="lm-fade" style={{ animationDelay: '0.04s' }}>
                  <h2
                    style={{
                      fontSize: '22px',
                      fontWeight: 800,
                      letterSpacing: '-0.03em',
                      color: 'var(--ui-ink)',
                      lineHeight: 1.15,
                      marginBottom: '6px',
                    }}
                  >
                    Sign in with your phone
                  </h2>
                  <p
                    className="mb-4"
                    style={{
                      fontSize: '13px',
                      color: 'var(--ui-ink-2)',
                      lineHeight: 1.5,
                    }}
                  >
                    We'll text you a 4-digit code. No passwords needed.
                  </p>
                  <LoginForm onGetCode={handlePhoneSubmit} />
                </div>
              )}

              {step === 2 && (
                <div className="lm-fade" style={{ animationDelay: '0.04s' }}>
                  <h2
                    style={{
                      fontSize: '22px',
                      fontWeight: 800,
                      letterSpacing: '-0.03em',
                      color: 'var(--ui-ink)',
                      lineHeight: 1.15,
                      marginBottom: '6px',
                    }}
                  >
                    Enter your code
                  </h2>
                  <p
                    className="mb-4"
                    style={{
                      fontSize: '13px',
                      color: 'var(--ui-ink-2)',
                      lineHeight: 1.5,
                    }}
                  >
                    We sent a 4-digit code to{' '}
                    <strong style={{ color: 'var(--ui-ink)', fontWeight: 700 }}>
                      {phoneMasked}
                    </strong>
                    .
                  </p>
                  <CodeForm phone={phone} onLogin={handleCodeSubmit} />
                </div>
              )}

              {step === 3 && (
                <div className="lm-fade" style={{ animationDelay: '0.04s' }}>
                  <h2
                    style={{
                      fontSize: '22px',
                      fontWeight: 800,
                      letterSpacing: '-0.03em',
                      color: 'var(--ui-ink)',
                      lineHeight: 1.15,
                      marginBottom: '6px',
                    }}
                  >
                    Complete your profile
                  </h2>
                  <p
                    className="mb-4"
                    style={{
                      fontSize: '13px',
                      color: 'var(--ui-ink-2)',
                      lineHeight: 1.5,
                    }}
                  >
                    Just a couple quick details to personalize your orders.
                  </p>
                  <CompleteForm
                    onComplete={handleCompleteSubmit}
                    onSkip={handleSkip}
                  />
                </div>
              )}

              {/* Trust footer */}
              {step !== 3 && (
                <div
                  className="flex items-center justify-center gap-[6px] mt-4"
                  style={{ fontSize: '11.5px', color: 'var(--ui-ink-3)' }}
                >
                  <HiOutlineLockClosed size={12} />
                  <span>Secure phone-only sign-in</span>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Dialog>
  );
};

const SuccessFlash = () => (
  <div
    className="flex flex-col items-center justify-center py-12 lm-fade"
    style={{ animationDelay: '0s' }}
  >
    <div
      className="lm-pop grid place-items-center mb-4"
      style={{
        width: '72px',
        height: '72px',
        borderRadius: '50%',
        background: 'rgba(21, 128, 61, 0.12)',
        color: 'var(--ui-green)',
      }}
    >
      <div
        className="grid place-items-center"
        style={{
          width: '48px',
          height: '48px',
          borderRadius: '50%',
          background: 'var(--ui-green)',
          color: '#FFFFFF',
        }}
      >
        <HiOutlineCheck size={26} strokeWidth={3} />
      </div>
    </div>
    <div
      style={{
        fontSize: '18px',
        fontWeight: 800,
        letterSpacing: '-0.02em',
        color: 'var(--ui-ink)',
        marginBottom: '4px',
      }}
    >
      You're signed in
    </div>
    <div style={{ fontSize: '12.5px', color: 'var(--ui-ink-2)' }}>
      Redirecting you back…
    </div>
  </div>
);

export default LoginModal;
